import React, { useState, useEffect } from "react";
import { Files, Clipboard, Upload, Send, HelpCircle, CheckCircle, Database, Bell } from "lucide-react";
import { Order, Patient } from "../types";

import { connectSocket } from "../utils/socket";

interface LabXrayDashboardProps {
  user: { email: string; name: string; role: string };
  apiToken: string;
}

export default function LabXrayDashboard({ user, apiToken }: LabXrayDashboardProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Live Toast for new WebSockets events
  const [liveToast, setLiveToast] = useState<{message: string} | null>(null);

  // Results inputs
  const [resultsText, setResultsText] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const techRole = user.role; // LAB_TECH (Amir) or RADIOLOGY_TECH (Nawal)
  const deptFilter = techRole === "LAB_TECH" ? "LAB" : "X_RAY";

  useEffect(() => {
    // Connect to global socket
    const socket = connectSocket();

    socket.on("newOrder", (data: { department: string, body: Order }) => {
      if (data.department === deptFilter) {
        // Auto trigger fetch
        fetchOrdersAndPatients();
        // Show live popup
        setLiveToast({ message: `New ${data.department} request received!` });
        setTimeout(() => setLiveToast(null), 5000);
      }
    });

    socket.on("databaseSynchronised", () => {
      fetchOrdersAndPatients();
    });

    return () => {
      socket.disconnect();
    };
  }, [deptFilter]);

  useEffect(() => {
    fetchOrdersAndPatients();
  }, [apiToken]);

  const fetchOrdersAndPatients = async () => {
    try {
      // Fetch Orders
      const oRes = await fetch("/api/orders", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const oData = await oRes.json();

      // Fetch Patients
      const pRes = await fetch("/api/patients", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const pData = await pRes.json();

      if (oRes.ok && pRes.ok) {
        setOrders(oData);
        setPatients(pData);
        
        // Filter standard department order
        const deptFilter = techRole === "LAB_TECH" ? "LAB" : "X_RAY";
        const incoming = oData.filter((o: Order) => o.department === deptFilter && o.status === "PENDING");
        if (incoming.length > 0 && !selectedOrder) {
          setSelectedOrder(incoming[0]);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handlePublishResults = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrder) return;
    setLoading(true);

    const payloadResults = fileUrl 
      ? `Result Parameters:\n${resultsText}\n\nAttached Diagnostic URL / DICOM: ${fileUrl}`
      : resultsText;

    try {
      const res = await fetch(`/api/orders/${selectedOrder.id}/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          status: "COMPLETED",
          results: payloadResults,
        }),
      });

      if (res.ok) {
        setSuccessMsg("Results published to physician EHR system successfully!");
        setResultsText("");
        setFileUrl("");
        setSelectedOrder(null);
        await fetchOrdersAndPatients();
        setTimeout(() => setSuccessMsg(""), 4000);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectPredefinedResult = (prefill: string, mockLink?: string) => {
    setResultsText(prefill);
    if (mockLink) {
      setFileUrl(mockLink);
    }
  };

  // Filter queue matching technologist specialty node
  const categoryMapped = techRole === "LAB_TECH" ? "LAB" : "X_RAY";
  const filteredOrders = orders.filter((o) => o.department === categoryMapped);
  const pendingCount = filteredOrders.filter((o) => o.status === "PENDING").length;

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6">
      {/* Websocket Live Alert Notification */}
      {liveToast && (
        <div className="fixed top-20 right-6 z-50 bg-indigo-600 text-white font-bold text-xs px-5 py-4 rounded shadow-2xl flex items-center justify-between gap-3 animate-bounce">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-indigo-200" />
            <span>{liveToast.message}</span>
          </div>
        </div>
      )}

      {/* Toast Alert */}
      {successMsg && (
        <div className="fixed top-6 right-6 z-50 bg-[#0F172A] text-white font-medium text-xs px-4 py-3 rounded-md shadow-md border border-[#E2E8F0] flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-[#10B981]" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Header Panel */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <span className="bg-[#F8FAFC] text-[#64748B] font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-[#E2E8F0]">
            {techRole === "LAB_TECH" ? "Laboratory Division" : "Medical Imaging (Radiology) Division"}
          </span>
          <h1 className="text-base font-bold text-[#1E293B] mt-2">
            {techRole === "LAB_TECH" ? "Clinical Lab Diagnostic Console" : "PACS & Radiology Imaging Terminal"}
          </h1>
          <p className="text-[#64748B] text-xs font-medium">Logged in as {user.name} • {techRole === "LAB_TECH" ? "Amir Node" : "Nawal Node"}</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] text-[#0F172A] text-xs px-3 py-1.5 rounded-md flex items-center gap-2 font-bold select-none">
          <Database className="w-4 h-4" />
          <span>Pending Orders Hierarchy: {pendingCount}</span>
        </div>
      </div>

      {/* Summary Statistics Widget */}


      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: List of incoming orders */}
        <div className="col-span-1 border border-[#E2E8F0] rounded-lg p-4 shadow-sm bg-white">
          <h3 className="font-bold text-[#1E293B] text-xs mb-4 flex items-center gap-2 pb-2 uppercase tracking-wide text-[#64748B] border-b border-[#F1F5F9]">
            <Files className="w-4 h-4 text-[#0F172A]" />
            Directives Ledger Queue
          </h3>

          {filteredOrders.length > 0 ? (
            <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-1">
              {filteredOrders.map((order) => {
                const patObj = patients.find((p) => p.id === order.patientId);
                const isSelected = selectedOrder?.id === order.id;

                return (
                  <div
                    key={order.id}
                    onClick={() => {
                      setSelectedOrder(order);
                      setResultsText("");
                      setFileUrl("");
                    }}
                    className={`p-3 rounded-md border cursor-pointer select-none transition flex flex-col gap-1 ${isSelected ? 'bg-indigo-50 border-indigo-200 shadow-inner' : 'bg-white border-[#E2E8F0] hover:bg-[#F8FAFC]'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-[#64748B] font-mono">Order: {order.id.slice(0, 8)}</span>
                      <span className={`font-bold text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                        order.status === "PENDING"
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-purple-50 text-purple-700 border-purple-200"
                      }`}>
                        {order.status}
                      </span>
                    </div>
                    <strong className="text-[#1E293B] text-xs mt-1 block font-semibold">{patObj ? patObj.name : "Unregistered Patient"}</strong>
                    <p className="text-[#64748B] text-xs truncate mt-0.5 line-clamp-1 font-medium">"{order.instruction}"</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 border border-dashed border-[#E2E8F0] rounded-md">
              <Clipboard className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider text-[#64748B]">No Active Directives</h4>
              <p className="text-[#64748B] text-xs mt-1 px-4 font-medium">All clinical parameters were logged and certified.</p>
            </div>
          )}
        </div>

        {/* Right Column: Execution Form */}
        <div className="col-span-1 lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm">
          {selectedOrder ? (
            <form onSubmit={handlePublishResults} className="space-y-6">
              <div className="bg-[#F8FAFC] rounded-md p-4 border border-[#E2E8F0] relative overflow-hidden">
                <div className="flex justify-between items-center mb-1">
                  <div className="text-[9px] uppercase font-bold text-[#64748B]">Physician Instruction Sheet</div>
                  <button 
                    type="button"
                    onClick={() => setSelectedOrder(null)}
                    className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition"
                  >
                    Close Record
                  </button>
                </div>
                <h4 className="text-xs font-bold text-[#1E293B]">
                  Target for patient: {patients.find((p) => p.id === selectedOrder.patientId)?.name}
                </h4>
                <p className="text-xs text-[#1E293B] font-semibold bg-white border border-[#E2E8F0] p-3 rounded-md mt-3 leading-relaxed whitespace-pre-wrap">
                  "{selectedOrder.instruction}"
                </p>
                <span className="text-[9px] text-[#64748B] font-mono mt-2 block">Physician Author: {selectedOrder.physicianId}</span>
              </div>

              {/* Seeding diagnostic helpers */}
              <div className="p-3 bg-white border border-[#E2E8F0] rounded-md">
                <span className="text-[9px] uppercase font-bold text-[#64748B] block mb-2">Click to Prefill Sample Parameters:</span>
                <div className="flex flex-wrap gap-2 text-[10px] font-semibold">
                  {techRole === "LAB_TECH" ? (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          selectPredefinedResult(
                            "Fasting Blood Glucose (FBG): 108 mg/dL (Borderline High)\nHbA1c: 5.6% (Normoglycemia)\nBlood Urea: 24 mg/dL\nS. Creatinine: 0.82 mg/dL"
                          )
                        }
                        className="bg-white hover:bg-[#F8FAFC] px-2.5 py-1 rounded border border-[#E2E8F0] text-[#0F172A] cursor-pointer"
                      >
                        Glycemic / Renal Panel
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          selectPredefinedResult(
                            "Hemoglobin (Hb): 13.8 g/dL\nWhite Blood Cells (WBC): 6.4 x10^9/L\nPlatelets: 245 x10^9/L"
                          )
                        }
                        className="bg-white hover:bg-[#F8FAFC] px-2.5 py-1 rounded border border-[#E2E8F0] text-[#0F172A] cursor-pointer"
                      >
                        C.B.C Results prefill
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          selectPredefinedResult(
                            "Chest PA View: Clear lung fields bilaterally. Cardiothoracic ratio is normal. No structural parenchymal lesions, pleural effusion, or pneumothorax visible.",
                            "https://ais-dev-placeholder-dicom-lung-xray.jpg"
                          )
                        }
                        className="bg-white hover:bg-[#F8FAFC] px-2.5 py-1 rounded border border-[#E2E8F0] text-[#0F172A] cursor-pointer"
                      >
                        Lung Chest X-Ray DICOM
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          selectPredefinedResult(
                            "Facial Soft Tissue Ultrasound: Hyaluronic acid micro-depots localized evenly within sub-dermal levels. No filler embolisation, vascular compression, or abscess formation identified.",
                            "https://ais-dev-placeholder-soft-tissue-ultra.jpg"
                          )
                        }
                        className="bg-white hover:bg-[#F8FAFC] px-2.5 py-1 rounded border border-[#E2E8F0] text-[#0F172A] cursor-pointer"
                      >
                        Cosmetic Facial Ultrasound
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">
                    Diagnostic Reports & Quantitative Values
                  </label>
                  <textarea
                    rows={5}
                    value={resultsText}
                    onChange={(e) => setResultsText(e.target.value)}
                    placeholder="Type lab findings, blood counts, liver parameters OR radiology interpretation reports..."
                    className="w-full bg-white border border-[#E2E8F0] text-xs font-semibold rounded-md p-3 outline-none focus:border-[#0F172A] text-[#1E293B]"
                    required
                  />
                </div>

                {techRole === "RADIOLOGY_TECH" && (
                  <div>
                    <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">
                      DICOM Reference / PACS Reference Link
                    </label>
                    <div className="relative flex items-center">
                      <input
                        type="text"
                        value={fileUrl}
                        onChange={(e) => setFileUrl(e.target.value)}
                        placeholder="https://pacs-system.clinic.iq/instances/dicom-77a..."
                        className="w-full bg-white border border-[#E2E8F0] text-[#1E293B] font-mono text-xs px-3 py-2 rounded-md outline-none focus:border-[#0F172A]"
                      />
                      <Upload className="w-3.5 h-3.5 text-[#64748B] absolute right-3" />
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-[#0F172A] hover:bg-slate-800 text-white font-bold text-xs rounded-md flex items-center justify-center gap-2 cursor-pointer transition select-none"
              >
                <Send className="w-3.5 h-3.5" />
                {loading ? "Transmitting..." : "Publish Diagnostic Report to EHR Flow"}
              </button>
            </form>
          ) : (
            <div className="h-full flex flex-col justify-center items-center text-center p-12 py-24">
              <Clipboard className="w-10 h-10 text-slate-300 mb-3" />
              <h3 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider text-[#64748B]">Select Diagnostic Directive</h3>
              <p className="text-[#64748B] text-xs max-w-xs leading-relaxed mt-1 font-medium">
                Select an order item from the incoming queue on the left column to execute values acquisition and PACS uploading.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
